import { Nullable } from "common/records";
import { EstimateGroupRecord } from "common/records/EstimateGroup";
import { RootState } from "common/reducers";
import { currentEstimateGroupIds } from "common/selectors/estimate.selectors";
import { List } from "immutable";
import { createSelector } from "reselect";

const rootKey = "estimateGroups";

/**
 * Loads byId key from estimateGroups
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, EstimateGroupRecord>} Map<number, EstimateGroupRecord>
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Loads all estimate groups sorted by included DESC then sort_order ASC
 *
 * @param {RootState} state The RootState
 * @returns {List<EstimateGroupRecord>} List<EstimateGroupRecord>
 */
export const currentEstimateGroups = createSelector([byId, currentEstimateGroupIds], (byId, groupIds) => {
  const groups = groupIds
    .map((id) => byId.get(id))
    .filter((group) => group && !group._destroy) as List<EstimateGroupRecord>;
  return groups.sortBy(
    (group) => [group.included ? 0 : 1, group.sort_order ?? 0],
    (a, b) => {
      if (a[0] !== b[0]) return a[0] - b[0];
      return (a[1] as number) - (b[1] as number);
    },
  ) as List<EstimateGroupRecord>;
});

export const currentIncludedEstimateGroupIds = createSelector([currentEstimateGroups], (groups) =>
  groups.filter((group) => group.included).map((group) => group.id),
);

export const currentOptionalEstimateGroupIds = createSelector([currentEstimateGroups], (groups) =>
  groups.filter((group) => !group.included).map((group) => group.id),
);

/**
 * Loads the estimate group by id
 *
 * @param {RootState} state The RootState
 * @param {{ estimateGroupId: number }} props The estimate group id
 * @returns {EstimateGroupRecord} Nullable<EstimateGroupRecord>
 */
export const estimateGroup = (state: RootState, props: { estimateGroupId: Nullable<number> }) =>
  props.estimateGroupId ? byId(state).get(props.estimateGroupId) : undefined;

/**
 * Loads the estimate group line item ids
 *
 *  @param {RootState} state The RootState
 * @param {{ estimateGroupId: number }} props The estimate group id
 * @returns {List<number>} List<number>
 */
export const estimateGroupLineItemIds = (state: RootState, props: { estimateGroupId: number }) =>
  estimateGroup(state, props)?.get("line_item_ids") || List();

/**
 * Loads the estimate groups by estimateGroupIds
 *
 * @param {RootState} state The RootState
 * @param {{ estimateGroupIds: List<number> }} props The estimate group ids
 * @returns {List<EstimateGroupRecord>} List<EstimateGroupRecord>
 */
export const estimateGroups = (state: RootState, props: { estimateGroupIds: List<number> }) =>
  props.estimateGroupIds.map((id) => estimateGroup(state, { estimateGroupId: id }));

/**
 * Returns a filtered list of estimate groups by their ids and filter options.
 *
 * This selector retrieves estimate groups from the state using the provided ids, and applies filtering based on the `included` and `fullEstimate` options:
 * - If `included` is specified, only groups matching the included status are returned (unless `fullEstimate` is true).
 * - If `fullEstimate` is true, all groups are returned regardless of their status or `_destroy` flag.
 * - Groups with `_destroy` set to true are excluded unless `fullEstimate` is true.
 *
 * @param {RootState} state - The Redux root state
 * @param {{ estimateGroupIds: List<number>; included?: boolean; fullEstimate?: boolean }} props - Filtering options and group ids
 * @returns {List<EstimateGroupRecord>} List of filtered EstimateGroupRecords
 */
export const filteredEstimateGroups = createSelector(
  [estimateGroups, (_, props: { estimateGroupIds: List<number>; included?: boolean; fullEstimate?: boolean }) => props],
  (groups, { included = true, fullEstimate = false }) => {
    return groups.filter((group) => {
      return group && (fullEstimate || (!group._destroy && group.included === included));
    }) as List<EstimateGroupRecord>;
  },
);

/**
 * Gets the current unsaved ID for creating new estimate groups
 *
 * @param {RootState} state The RootState
 * @returns {number} The current unsaved ID (negative number)
 */
export const unsavedId = (state: RootState): number => state.getIn([rootKey, "unsavedId"]) as number;

/**
 * Gets the group ID currently being renamed, or null if none
 *
 * @param {RootState} state The RootState
 * @returns {number | null}
 */
export const renamingGroupId = (state: RootState): number | null =>
  state.getIn([rootKey, "renamingGroupId"]) as number | null;

/**
 * Gets the draft name for the group currently being renamed
 *
 * @param {RootState} state The RootState
 * @returns {string}
 */
export const renamingGroupDraftName = (state: RootState): string =>
  state.getIn([rootKey, "renamingGroupDraftName"]) as string;
